import { Button, TextInput, Textarea, Switch, Text, Title, FileInput, SimpleGrid, Stack, Paper, Group } from "@mantine/core";
import { Upload } from "lucide-react";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Head from "../../components/common/Head";
import useValidate from "../../hooks/useValidate";
import { addCreator, getCreatorById, updateCreator } from "../../utils/api";

const AddCreator = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [profilePic, setProfilePic] = useState(null);

  useValidate();

  useEffect(() => {
    if (id) fetchCreator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCreator = async () => {
    setLoading(true);
    const [success, response] = await getCreatorById(id);
    if (success) {
      setName(response.name || "");
      setPhoneNumber(response.phoneNumber || "");
      setEmail(response.email || "");
      setDescription(response.description || "");
      setIsEnabled(!response.isDisable);
      setIsFeatured(response.isFeatured || false);
    } else {
      notifications.show({ message: "Failed to fetch partner details", color: "red" });
      navigate("/partners");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("phoneNumber", phoneNumber);
    formData.append("email", email);
    formData.append("description", description);
    formData.append("isFeatured", isFeatured);
    if (id) formData.append("isDisable", !isEnabled);
    if (profilePic) formData.append("image", profilePic);

    const [success] = id
      ? await updateCreator(id, formData)
      : await addCreator(formData);

    if (success) {
      notifications.show({ message: id ? "Partner updated successfully" : "Partner added successfully", color: "green" });
      navigate("/partners");
    } else {
      notifications.show({ message: "Failed to save partner", color: "red" });
    }
    setLoading(false);
  };

  return (
    <>
      <Head title={id ? "Edit Partner" : "Add Partner"} back="/partners" loading={loading} />

      <Paper withBorder p="lg">
        <form onSubmit={handleSubmit}>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Stack gap="md">
              <Title order={5}>Basic Information</Title>
              <TextInput
                label="Partner Name"
                placeholder="Enter partner name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextInput
                label="Phone Number"
                placeholder="Enter phone number"
                required
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <TextInput
                label="Email"
                placeholder="Enter email address"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FileInput
                label="Profile Picture"
                placeholder="Upload image"
                accept="image/*"
                leftSection={<Upload size={16} />}
                value={profilePic}
                onChange={(file) => {
                  if (file && file.size > 2 * 1024 * 1024) {
                    notifications.show({ message: "Image must be smaller than 2MB!", color: "red" });
                    return;
                  }
                  setProfilePic(file);
                }}
              />
            </Stack>

            <Stack gap="md">
              <Title order={5}>Additional Details</Title>
              <Textarea
                label="Description"
                placeholder="Enter description"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Switch
                label="Featured Partner"
                checked={isFeatured}
                onChange={(event) => setIsFeatured(event.currentTarget.checked)}
              />
              {id && (
                <Switch
                  label={isEnabled ? "Enabled" : "Disabled"}
                  checked={isEnabled}
                  onChange={(event) => setIsEnabled(event.currentTarget.checked)}
                />
              )}
            </Stack>
          </SimpleGrid>

          <Group justify="flex-end" mt="xl">
            <Button type="submit" loading={loading} size="md">
              {id ? "Update Partner" : "Add Partner"}
            </Button>
          </Group>
        </form>
      </Paper>
    </>
  );
};

export default AddCreator;
